<template>
  <div v-show="isOpen || $vuetify.display.smAndDown" class="restrictions-table">
    <div class="d-flex justify-space-between align-center mb-6 mb-md-4">
      <div class="h4 mr-2">Таблица ограничений</div>
      <e-btn v-if="$vuetify.display.mdAndUp" variant="ghost" size="M" icon @click="isOpen = false">
        <e-icon name="closeSvg" size="20" />
      </e-btn>
      <e-btn v-else variant="primary" prepend-icon-src="calendar" @click="emit('open:chess')"> Шахматка </e-btn>
    </div>

    <div class="restrictions-table__head">
      <e-date-picker v-model="form.period.from" label="Период с" :min="props.period.from" :max="form.period.to" />
      <e-date-picker v-model="form.period.to" label="Период по" :min="form.period.from" :max="props.period.to" />
      <e-btn
        v-if="slots.filters"
        :variant="isFiltersOpen ? 'tonal' : 'ghost'"
        icon
        prepend-icon-src="options"
        @click="setIsFiltersOpen(!isFiltersOpen)"
      />
    </div>

    <div v-show="isFiltersOpen" class="restrictions-table__filters">
      <slot name="filters" />
    </div>

    <div class="mt-6">
      <e-data-table
        v-if="filteredItems.length"
        :headers="props.tableHeaders"
        :items="filteredItems"
        interactive
        small
        vertical-align="top"
        @on-row-click="onRowClick"
      >
        <template v-for="(_, slotName) in internalSlots" v-slot:[slotName]="slotData">
          <slot v-if="slotName" :name="slotName" v-bind="slotData" />
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <context-menu>
            <div class="d-flex flex-column pa-2">
              <e-btn
                class="justify-start"
                size="M"
                variant="context"
                prepend-icon-src="calendar"
                prepend-icon-size="20"
                @click="emit('show', item)"
              >
                На шахматке
              </e-btn>
              <template v-if="item.period.to >= dayjs(Date.now()).format('YYYY-MM-DD')">
                <operation-button
                  class="justify-start"
                  size="M"
                  variant="context"
                  prepend-icon-src="editPen"
                  prepend-icon-size="20"
                  :operation="operationEdit"
                  @click="emit('edit', item)"
                >
                  Редактировать
                </operation-button>
                <operation-button
                  class="justify-start text-red"
                  size="M"
                  variant="context"
                  prepend-icon-src="trash"
                  prepend-icon-size="20"
                  :operation="operationDelete"
                  @click="emit('remove', item)"
                >
                  Удалить
                </operation-button>
              </template>
            </div>
          </context-menu>
        </template>
      </e-data-table>
      <div v-else class="d-flex flex-column align-center">
        <v-img width="160" height="80" :src="NoDataImage" />
        <div class="caption text-center mt-6">
          Ограничений в выбранном <br />
          периоде не найдено
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="K extends Record<string, unknown>, T extends RestrictionsTableItem<K>">
import dayjs from 'dayjs';
import { computed, reactive, watch } from 'vue';
import type { RangeOfDate } from '@/core/types/common';
import type { EDataItemsOnRowClickItem } from '@/core/types/components/components';
import type { DataTableHeaders } from '@/core/types/components/table';
import type { RestrictionsTableItem } from '@/parts/tariffs/types/tariffs';
import NoDataImage from '@/assets/img/common/no-data.svg';
import OperationButton from '@/core/components/buttons/OperationButton.vue';
import ContextMenu from '@/core/components/common/ContextMenu.vue';
import { EBtn, EDataTable, EDatePicker, EIcon } from '@/core/components/ui';

import useToggle from '@/core/composables/useToggle';
import { OperationStatus } from '@/graphql/generated';

const props = withDefaults(
  defineProps<{
    period?: RangeOfDate;
    tableHeaders: DataTableHeaders;
    tableItems: T[];
    operationEdit?: OperationStatus | null | undefined;
    operationDelete?: OperationStatus | null | undefined;
  }>(),
  {
    period: () => ({
      from: dayjs().format('YYYY-MM-DD'),
      to: dayjs().format('YYYY-MM-DD'),
    }),
  },
);

const emit = defineEmits<{
  (event: 'show', value: T): void;
  (event: 'edit', value: T): void;
  (event: 'remove', value: T): void;
  (event: 'open:chess'): void;
}>();

const slots = defineSlots<
  {
    filters?: () => void;
  } & { [K in `item.${string}`]: (props: { item: T }) => void }
>();

const internalSlots = computed<{ [K in `item.${string}`]: (props: { item: T }) => void }>(() => {
  const s = { ...slots };

  delete s.filters;

  return s;
});

const isOpen = defineModel('open');

const [isFiltersOpen, setIsFiltersOpen] = useToggle();

const form = reactive({
  period: {
    from: new Date(),
    to: new Date(),
  },
});

watch(
  () => props.period,
  () => {
    form.period = {
      from: props.period.from ? new Date(props.period.from) : new Date(),
      to: props.period.to ? new Date(props.period.to) : new Date(),
    };
  },
  { immediate: true },
);

const filteredItems = computed(() => {
  return props.tableItems.filter((item) => {
    return new Date(item.period.to) >= form.period.from && new Date(item.period.from) <= form.period.to;
  });
});

const onRowClick = (_: Event, tableItem: EDataItemsOnRowClickItem<T>) => {
  emit('show', tableItem.item);
};
</script>

<style scoped lang="scss">
.restrictions-table {
  flex-shrink: 0;

  width: 100%;

  @include md {
    margin-bottom: 24px;
  }

  @include lg-and-up {
    width: 420px;
    margin-left: 40px;
  }

  @include xl {
    width: 480px;
  }

  &__head {
    display: grid;

    grid-gap: 16px;
    grid-template-columns: repeat(2, minmax(0, 1fr)) auto;

    @include md {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__filters {
    display: grid;

    grid-gap: 16px;

    margin-top: 16px;

    @include md {
      grid-template-columns: repeat(3, 1fr);
    }
  }
}
</style>
